import { takeLatest, select, put, call } from 'redux-saga/effects';
import {
  setQueueTxnOpps,
  fetchQueueTxnOppsData,
  setQueueBinsState,
  setIsFetchingQueueTxnOppsData,
  appNotify,
  updateClientTxnOppsFromQueued,
  setOriginalMyTasksOrder
} from 'store/actions';
import {
  userSelector,
  signalRQueuedUpdatesSelector,
  queueSortInputSelector,
  showPostAcquisitionsSelector,
} from 'store/selectors';
import { getWithToken, postWithToken } from 'api/network';
import { sendClientLog } from 'services/logService';
import { Constants } from 'utilities/constants';
import Query from 'devextreme/data/query';
import { CheckTaskCompleted, PostAcqCheckTaskCompleted } from 'services/taskManagementService';

const {
  OpportunityStatus: { COMPLETED, TERMINATED },
  SignalRUpdateTarget: { QUEUETXNOPPS },
} = Constants;

export function txnOppFitsInQueue(txnOpp, userId, showPostAcquisitions) {
  const isUserTask = txnOpp.userId != null && txnOpp.userId === userId;
  if (!isUserTask) return false; // Task must belong to user

  if (txnOpp.opportunityStatus === TERMINATED) return false; // Task cannot be terminated

  if (txnOpp.opportunityStatus === COMPLETED) {
    return showPostAcquisitions ? txnOpp.isPostAcquisitionUnderwrite : false; // Tasks in Completed opp status are only shown in post-acq mode
  }
  return true;
}

export function postAcqTaskFitsInQueue(task, userId, includeUnloaded) {
  const isUserTask = task.assignedUserId != null && task.assignedUserId === userId;
  if (!isUserTask) return false; // Task must belong to user

  if (!includeUnloaded && task.postAcquisitionLoadInstanceId == null) return false; // Task must be loaded
  return true;
}

export function* configureQueueBinsState(module, queueItems, showPostAcquisitions) {
  const user = yield select(userSelector);
  let filteredQueueItems;
  if (module === Constants.Module.UNDERWRITE) {
    filteredQueueItems = queueItems
      .filter(opp => txnOppFitsInQueue(opp, user.id, showPostAcquisitions))
      .map(opp => { return { ...opp, taskType: 'txnOpp' } });
  } else if (module === Constants.Module.POSTACQ) {
    filteredQueueItems = queueItems
      .filter(opp => postAcqTaskFitsInQueue(opp, user.id))
      .map(opp => { return { ...opp, taskType: 'postAcqTask' } });
  }

  const queueSortInput = yield select(queueSortInputSelector, module);
  const sortInfo = queueSortInput.filter(x => x.order !== null);
  if(sortInfo.length > 0){
    var query = Query(filteredQueueItems).sortBy(sortInfo[0].label, sortInfo[0].order === "desc");
    for(var i = 1; i < sortInfo.length; i++){
      query = query.thenBy(sortInfo[i].label, sortInfo[i].order === "desc");
    }
    filteredQueueItems = query.toArray(); 
  }

  const tasks =  filteredQueueItems;
  const taskCompleted = module === Constants.Module.POSTACQ ? PostAcqCheckTaskCompleted : CheckTaskCompleted;
  const queueBinsState = {
    allTasks: {
      id: 'allTasks',
      title: 'All Tasks',
      tasks: tasks,
      isAnyTaskCompleted : function () { return tasks.some(el => taskCompleted(el, showPostAcquisitions)); },
      completedTasksLength : function () { return tasks.filter(el => taskCompleted(el, showPostAcquisitions)).length; },
      openTasksLength : function () { return tasks.filter(el => !taskCompleted(el, showPostAcquisitions)).length; },
      areSame: function(newTasks) {
        if(tasks.length !== newTasks?.tasks.length) {
          return false;
        }
        var areSame = true;
        const acqTest = (el, newEl) => {
          return showPostAcquisitions ? newEl.valuationsPostAcquisitionCount === el.valuationsPostAcquisitionCount : newEl.underwriteStatus === el.underwriteStatus;
        }
        const postAcqTest = (el, newEl) => {
          return newEl.taskStatus === el.taskStatus && newEl.underwriteStatus === el.underwriteStatus;
        }
        tasks.forEach(element => {
          if(!newTasks?.tasks.some(newEl => newEl.id === element.id && (module === Constants.Module.POSTACQ ? postAcqTest(element, newEl) : acqTest(element, newEl)))){
            areSame = false;
          }
        });
        return areSame;
      }
    }
  };

  return queueBinsState;
}

function* fetchData({ payload: module }) {
  try {
    yield put(setIsFetchingQueueTxnOppsData(module, true));
    const startTime = new Date();

    const showPostAcquisitions = yield select(showPostAcquisitionsSelector)
    let data;
    if (module === Constants.Module.UNDERWRITE) {
      const forUserQueueRequest = { showPostAcquisitions }
      const res = yield postWithToken(`/api/underwrite/workflow/opportunity/forUserQueue`, forUserQueueRequest);
      data = yield res.json();
    } else if (module === Constants.Module.POSTACQ) {
      const res = yield getWithToken(`/api/postAcquisition/tasks/forUserQueue`);
      data = yield res.json();
    }
    let newData = data;

    const queueBinsState = yield call(configureQueueBinsState, module, newData, showPostAcquisitions);
    yield put(setQueueTxnOpps(module, newData));
    yield put(setQueueBinsState(module, queueBinsState));
    yield put(setOriginalMyTasksOrder(module, newData.map(task => task.id)));
    yield put(setIsFetchingQueueTxnOppsData(module, false));
    const signalRQueuedUpdates = yield select(signalRQueuedUpdatesSelector, module);
    if (signalRQueuedUpdates.length) {
      yield put(updateClientTxnOppsFromQueued(QUEUETXNOPPS));
    }
    const endTime = new Date();
    const secondsElapsed = (endTime - startTime) / 1000;
    const speed = newData.length > 0 && secondsElapsed > 0 ? newData.length / secondsElapsed : null;
    sendClientLog('Info', `API - fetched ${newData.length} queueTxnOpps in ${secondsElapsed} seconds${speed ? ` (${speed.toFixed()}/sec)` : ''}`);
  }
  catch (error) {
    yield put(setIsFetchingQueueTxnOppsData(module, false));
    yield put(appNotify(error?.message, 'error'));
  }
}

export function* getQueueTxnOppsDataSaga() {
  yield takeLatest(fetchQueueTxnOppsData, fetchData);
}